import type {
  Archive,
  PodcastSeries,
  PodcastEpisode,
  YouTubeChannel,
  YouTubeVideo,
  Theme,
  Artifact,
  LatestContent,
} from '@/types'

// API base URL - hardcoded for production (v2)
const API_BASE = 'https://api.thearchivenetwork.com/api'

async function fetchJson<T>(path: string): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`)
  if (!response.ok) {
    throw new Error(`API error: ${response.status} ${response.statusText}`)
  }
  return response.json()
}

// Archives
//
// The archive list is requested independently by HomePage, HeroRotunda and
// Footer, all of which mount together — three identical /archives round trips
// per navigation (Footer is on every page). The list is small and effectively
// static for a session, so share one in-flight promise. A rejection clears the
// cache so a later attempt can retry rather than replaying the failure.
let archivesPromise: Promise<Archive[]> | null = null

export async function getArchives(): Promise<Archive[]> {
  if (!archivesPromise) {
    archivesPromise = fetchJson<Archive[]>('/archives').catch((err) => {
      archivesPromise = null
      throw err
    })
  }
  return archivesPromise
}

export async function getArchive(key: string): Promise<Archive> {
  return fetchJson<Archive>(`/archives/${key}`)
}

// Contact — POST to the single network-wide hub endpoint.
export interface ContactPayload {
  archive: string
  name: string
  email: string
  subject: string
  message: string
  company?: string // honeypot
}

export async function submitContact(
  payload: ContactPayload
): Promise<{ ok: boolean; error?: string }> {
  const response = await fetch(`${API_BASE}/contact`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  try {
    return (await response.json()) as { ok: boolean; error?: string }
  } catch {
    return { ok: response.ok, error: response.ok ? undefined : 'send_failed' }
  }
}

// Podcasts
export async function getPodcastSeries(): Promise<PodcastSeries[]> {
  return fetchJson<PodcastSeries[]>('/podcasts/series')
}

export async function getPodcastSeriesById(id: string): Promise<PodcastSeries> {
  return fetchJson<PodcastSeries>(`/podcasts/series/${id}`)
}

export interface PodcastEpisodeQuery {
  series?: string
  q?: string
  perSeries?: number
  limit?: number
  offset?: number
}

export async function getPodcastEpisodes(
  opts: PodcastEpisodeQuery = {}
): Promise<PodcastEpisode[]> {
  const params = new URLSearchParams()
  if (opts.series) params.set('series', opts.series)
  if (opts.q) params.set('q', opts.q)
  if (opts.perSeries != null) params.set('perSeries', String(opts.perSeries))
  if (opts.limit != null) params.set('limit', String(opts.limit))
  if (opts.offset != null) params.set('offset', String(opts.offset))
  const qs = params.toString()
  return fetchJson<PodcastEpisode[]>(`/podcasts/episodes${qs ? `?${qs}` : ''}`)
}

export async function getPodcastEpisode(id: string): Promise<PodcastEpisode> {
  return fetchJson<PodcastEpisode>(`/podcasts/episodes/${id}`)
}

// Videos
export async function getYouTubeChannels(): Promise<YouTubeChannel[]> {
  return fetchJson<YouTubeChannel[]>('/videos/channels')
}

export async function getYouTubeVideos(channelId?: string): Promise<YouTubeVideo[]> {
  const path = channelId ? `/videos?channel=${channelId}` : '/videos'
  return fetchJson<YouTubeVideo[]>(path)
}

export async function getYouTubeVideo(id: string): Promise<YouTubeVideo> {
  return fetchJson<YouTubeVideo>(`/videos/${id}`)
}

// Themes
export async function getThemes(): Promise<Theme[]> {
  return fetchJson<Theme[]>('/themes')
}

export async function getTheme(slug: string): Promise<Theme> {
  return fetchJson<Theme>(`/themes/${slug}`)
}

export async function getThemeContent(slug: string): Promise<{
  theme: Theme
  podcasts: PodcastEpisode[]
  videos: YouTubeVideo[]
  artifacts: Artifact[]
}> {
  return fetchJson(`/themes/${slug}/content`)
}

// Artifacts
export async function getArtifacts(params?: {
  archive?: string
  type?: string
  theme?: string
}): Promise<Artifact[]> {
  const searchParams = new URLSearchParams()
  if (params?.archive) searchParams.set('archive', params.archive)
  if (params?.type) searchParams.set('type', params.type)
  if (params?.theme) searchParams.set('theme', params.theme)

  const queryString = searchParams.toString()
  const path = queryString ? `/artifacts?${queryString}` : '/artifacts'
  return fetchJson<Artifact[]>(path)
}

export async function getArtifact(slug: string): Promise<Artifact> {
  return fetchJson<Artifact>(`/artifacts/${slug}`)
}

// Latest
export async function getLatest(): Promise<LatestContent> {
  return fetchJson<LatestContent>('/latest')
}

// Content by archive
export async function getArchiveContent(key: string): Promise<{
  archive: Archive
  podcasts: PodcastEpisode[]
  videos: YouTubeVideo[]
  artifacts: Artifact[]
}> {
  return fetchJson(`/archives/${key}/content`)
}

// Search
export interface SearchResult {
  archive_key: string
  archive_name: string
  slug: string
  name: string
  one_liner: string
  type: string
  score: number
  hero_image_key?: string
  url: string
}

export interface SearchResponse {
  ok: boolean
  query: string
  total: number
  results: SearchResult[]
  by_archive: Record<string, SearchResult[]>
}

export async function search(query: string, archive?: string): Promise<SearchResponse> {
  const params = new URLSearchParams({ q: query })
  if (archive) params.set('archive', archive)
  return fetchJson<SearchResponse>(`/search?${params.toString()}`)
}

export interface SearchSuggestion {
  label: string
  slug: string
  archive_key: string
}

export async function searchSuggest(query: string): Promise<{ suggestions: SearchSuggestion[] }> {
  return fetchJson<{ suggestions: SearchSuggestion[] }>(`/search/suggest?q=${encodeURIComponent(query)}`)
}
